
import { calculateTaskDays, workday } from '../../../components/Tasks/taskUtil'
import { usePostTask } from './usePostTask'
import { useCreateGoogleFolder } from '../../google/useCreateGoogleFolder'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import { useValidateGoogleToken } from '../../google/useValidateGoogleToken'
import dayjs from 'dayjs'
import useUploadTaskImages from './useUploadTaskImages'
import { useQueryClient } from '@tanstack/react-query'

type NewTaskProps = {
  formData: Record<string, any> // ✅ Task fields are dynamic, so we use Record<string, any>
  setIsCreating: (isCreating: boolean) => void // ✅ Function to toggle loading state
  onClose?: () => void // ✅ Callback function for closing UI after success
  parentFolderId?: string // ✅ Google Drive folder ID
}
type DataProps = {
  PK: string
  SK: string
  task_uuid: string
}

export const useSubmitTask = () => {
  const queryClient = useQueryClient()
  const { mutate: createTask } = usePostTask()
  const { mutateAsync: createTaskFolder } = useCreateGoogleFolder() // ✅ Async mutation for folder creation
  const { mutate: uploadImages } = useUploadTaskImages()
  const { validateToken } = useValidateGoogleToken()

  const submitNewTask = async ({ formData, setIsCreating, onClose, parentFolderId }: NewTaskProps) => {
    console.log('📌 FORM DATA:', formData)
    if (!formData.name || !formData.orgId) {
      console.warn('Name of folder and organization id required')
      return
    }
    try {
      setIsCreating(true)

      // ✅ 1. Validate Google Token before making folder request
      // const googleAccessToken = localStorage.getItem('google_access_token')
      // const userId = localStorage.getItem('slack_user_id')
      // const tokenResponse = await new Promise<{ isValid: boolean; accessToken: string | null }>(resolve => {
      //   validateToken(
      //     { accessToken: googleAccessToken, userId },
      //     {
      //       onSuccess: resolve,
      //       onError: error => {
      //         console.error('❌ Google Token Validation Failed:', error)
      //         resolve({ isValid: false, accessToken: null })
      //       },
      //     }
      //   )
      // })

      // if (!tokenResponse.isValid || !tokenResponse.accessToken) {
      //   console.error('❌ Invalid Google Token, cannot create folder.')
      //   setIsCreating(false)
      //   return
      // }

      // ✅ 2. Create Google Folder First
      const createdTime = dayjs().toISOString()

      const formattedCreatedTime = formatDateTime(createdTime)

      // const newFolder = await createTaskFolder({
      //   folderName: `${formData?.name} - ${formattedCreatedTime}`,
      //   parentFolderId, // ✅ Corrected to use function parameter
      // })

      // if (!newFolder?.folderId) {
      //   console.error('❌ Failed to create task folder in Google Drive')
      //   setIsCreating(false)
      //   return
      // }

      // console.log('✅ Google Folder Created:', newFolder.folderId)

      // ✅ 3. Create Task with the new Folder ID
      const taskDays =
        formData?.taskDays && formData?.taskDays > 0
          ? formData.taskDays
          : formData?.type
          ? calculateTaskDays(formData?.type)
          : 0

      const newDateDue =
        formData?.startDate && taskDays ? workday(formData?.startDate, taskDays) : formData?.endDate || null

      const updatedTask = {
        ...formData,
        PK: formData?.PK ?? `ORG_${formData?.orgId}`,
        ...(formData?.SK ? { SK: formData.SK } : {}),
        orgId: formData?.orgId,
        taskType: formData?.label,
        type: formData?.type,
        taskDays,
        dateDue: newDateDue,
        masterStatus: formData?.masterStatus ?? 'Not Started',
        createdTime,
        isMonthlyTask: formData?.isMonthlyTask ?? false,
        // taskFolderId: newFolder.folderId, // ✅ Save Folder ID in task
        // parentTaskFolderId: parentFolderId, // ✅ Keep reference to the parent folder
      }

      console.log('✅ Creating Task:', updatedTask)

      createTask(updatedTask as any, {
        onSuccess: (data) => {
          console.log('✅ Successfully created task', data)

          setIsCreating(false)
          queryClient.invalidateQueries({ queryKey: ['task', updatedTask?.PK, updatedTask?.SK] })
          onClose()
        },
        onError: error => {
          console.error('❌ Error creating task:', error)
          setIsCreating(false)
        },
      })
    } catch (error) {
      console.error('❌ Error in submitNewTask:', error)
      setIsCreating(false)
    }
  }

  return { submitNewTask }
}
